import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Receipt, Plus, Loader2, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { formatCameraAssegnata } from "../../utils/formatCamera";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_AUSILIO = [
  { value: "pannoloni", label: "Pannoloni" },
  { value: "traverse", label: "Traverse" },
];

const TIPI_SPESA = [
  { value: "parrucchiere", label: "Parrucchiere" },
  { value: "podologo", label: "Podologo" },
  { value: "visita_specialistica", label: "Visita Specialistica" },
  { value: "acquisto_ausili", label: "Acquisto Ausili" },
  { value: "altro", label: "Altro" },
];

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function BillingExclusionToggle({ id, checked, onCheckedChange }) {
  return (
    <div className={`rounded-xl border p-3 transition-colors ${
      checked ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"
    }`}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(v === true)}
          className="mt-0.5 h-5 w-5 rounded-md border-amber-300 data-[state=checked]:bg-amber-500 data-[state=checked]:text-white"
        />
        <Label htmlFor={id} className="flex-1 cursor-pointer">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <EyeOff className="h-4 w-4 text-amber-600" />
            Escludi dalla fatturazione
          </span>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            La voce resta registrata in Prima Nota, ma non verrà proposta nelle fatture.
          </span>
        </Label>
      </div>
    </div>
  );
}

export default function PrimaNotaRapidaDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const [ospiti, setOspiti] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("consumo");

  const [consumo, setConsumo] = useState({
    ospiteId: "", tipoAusilio: "", quantita: 1, data: todayISO(), note: "", escludiDaFatturazione: false,
  });
  const [spesa, setSpesa] = useState({
    ospiteId: "", tipo: "", importo: "", descrizione: "", data: todayISO(), note: "", escludiDaFatturazione: false,
  });

  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setOspiti(res.data || []))
      .catch(() => toast.error("Errore nel caricamento degli ospiti"));
  }, [open, token]);

  const ospitiAttivi = ospiti.filter((o) => !o.archiviata);

  const reset = () => {
    setTab("consumo");
    setConsumo({ ospiteId: "", tipoAusilio: "", quantita: 1, data: todayISO(), note: "", escludiDaFatturazione: false });
    setSpesa({ ospiteId: "", tipo: "", importo: "", descrizione: "", data: todayISO(), note: "", escludiDaFatturazione: false });
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSaveConsumo = async () => {
    if (!consumo.ospiteId) return toast.error("Seleziona un ospite");
    if (!consumo.tipoAusilio) return toast.error("Seleziona il tipo di ausilio");
    if (!consumo.quantita || parseInt(consumo.quantita, 10) < 1) return toast.error("Inserisci una quantità valida");
    if (!consumo.data) return toast.error("Seleziona la data");

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/prima-nota/consumi`, {
        ospiteId: consumo.ospiteId,
        tipoAusilio: consumo.tipoAusilio,
        quantita: parseInt(consumo.quantita, 10),
        data: consumo.data,
        note: consumo.note || null,
        escludiDaFatturazione: !!consumo.escludiDaFatturazione,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Consumo registrato");
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSpesa = async () => {
    if (!spesa.ospiteId) return toast.error("Seleziona un ospite");
    if (!spesa.tipo) return toast.error("Seleziona il tipo di spesa");
    if (!spesa.importo || parseFloat(spesa.importo) <= 0) return toast.error("Inserisci un importo valido");
    if (!spesa.data) return toast.error("Seleziona la data");

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/prima-nota/spese`, {
        ospiteId: spesa.ospiteId,
        tipo: spesa.tipo,
        importo: parseFloat(spesa.importo),
        descrizione: spesa.descrizione || null,
        data: spesa.data,
        note: spesa.note || null,
        escludiDaFatturazione: !!spesa.escludiDaFatturazione,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Spesa registrata");
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const renderOspiteSelect = (value, onChange) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder="Seleziona ospite..." /></SelectTrigger>
      <SelectContent>
        {ospitiAttivi.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            {o.nome_completo}
            {o.camera ? ` (${formatCameraAssegnata(o.camera) || o.camera})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-500" />
            Aggiungi spesa in Prima Nota
          </DialogTitle>
          <DialogDescription>
            Registra un consumo ausilio o una spesa extra direttamente da qui.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="consumo" className="gap-1.5">
              <Package className="w-4 h-4" />
              Consumo
            </TabsTrigger>
            <TabsTrigger value="spesa" className="gap-1.5">
              <Receipt className="w-4 h-4" />
              Spesa extra
            </TabsTrigger>
          </TabsList>

          <TabsContent value="consumo" className="space-y-4 py-2 mt-3">
            <div className="space-y-2">
              <Label>Ospite *</Label>
              {renderOspiteSelect(consumo.ospiteId, (v) => setConsumo((p) => ({ ...p, ospiteId: v })))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Tipo Ausilio *</Label>
                <Select value={consumo.tipoAusilio} onValueChange={(v) => setConsumo((p) => ({ ...p, tipoAusilio: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                  <SelectContent>
                    {TIPI_AUSILIO.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantità (pezzi) *</Label>
                <Input type="number" min="1" value={consumo.quantita}
                  onChange={(e) => setConsumo((p) => ({ ...p, quantita: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input type="date" value={consumo.data}
                  onChange={(e) => setConsumo((p) => ({ ...p, data: e.target.value }))} />
              </div>
            </div>
            <BillingExclusionToggle
              id="quick-consumo-escludi"
              checked={!!consumo.escludiDaFatturazione}
              onCheckedChange={(v) => setConsumo((p) => ({ ...p, escludiDaFatturazione: v }))}
            />
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={consumo.note} rows={2} placeholder="Note opzionali..."
                onChange={(e) => setConsumo((p) => ({ ...p, note: e.target.value }))} />
            </div>
          </TabsContent>

          <TabsContent value="spesa" className="space-y-4 py-2 mt-3">
            <div className="space-y-2">
              <Label>Ospite *</Label>
              {renderOspiteSelect(spesa.ospiteId, (v) => setSpesa((p) => ({ ...p, ospiteId: v })))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Tipo Spesa *</Label>
                <Select value={spesa.tipo} onValueChange={(v) => setSpesa((p) => ({ ...p, tipo: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                  <SelectContent>
                    {TIPI_SPESA.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Importo (€) *</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={spesa.importo}
                  onChange={(e) => setSpesa((p) => ({ ...p, importo: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input type="date" value={spesa.data}
                  onChange={(e) => setSpesa((p) => ({ ...p, data: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input placeholder="Descrizione opzionale..." value={spesa.descrizione}
                onChange={(e) => setSpesa((p) => ({ ...p, descrizione: e.target.value }))} />
            </div>
            <BillingExclusionToggle
              id="quick-spesa-escludi"
              checked={!!spesa.escludiDaFatturazione}
              onCheckedChange={(v) => setSpesa((p) => ({ ...p, escludiDaFatturazione: v }))}
            />
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={spesa.note} rows={2} placeholder="Note opzionali..."
                onChange={(e) => setSpesa((p) => ({ ...p, note: e.target.value }))} />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={saving}>Annulla</Button>
          <Button
            onClick={tab === "consumo" ? handleSaveConsumo : handleSaveSpesa}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Registra
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
